import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getMyAddresses,
  addMyAddress,
  updateMyAddress,
  updateMyProfile,
  getMyProfile,
} from "../services/userService";
import {
  createRazorpayOrder,
  verifyRazorpayPayment,
} from "../services/paymentService";
import { createOrder, getShippingQuote as getShippingQuoteApi } from "../services/orderService";
import {
  updateCart as apiUpdateCart,
  getCart as apiGetCart,
} from "../services/cartService";
import ConfirmModal from "../components/ConfirmModal";
import CheckoutPriceBreakdownSidebar from "../components/CheckoutPriceBreakdownSidebar";
import BillSidebar from "../components/BillSidebar";
import OrderConfirmationSidebar from "../components/OrderConfirmationSidebar";
import toast from "react-hot-toast";
import HomeMobileNavbar from "../components/HomeMobileNavbar";
import Navbar from "../components/Navbar";
import { validateCoupon } from "../services/couponService";
import { calculateShippingQuoteFromSubtotal } from "../utils/shippingUtils";

export default function Checkout() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newAddr, setNewAddr] = useState({
    label: "",
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    phone: "",
    isDefault: false,
  });
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editAddr, setEditAddr] = useState(null);
  const [confirm] = useState({ open: false });

  // PIN code lookup states
  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState("");
  const [editPinLoading, setEditPinLoading] = useState(false);
  const [editPinError, setEditPinError] = useState("");

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState("Razorpay"); // "Razorpay" or "COD"

  // New state for personal details editing
  const [editingPersonalDetails, setEditingPersonalDetails] = useState(false);
  const [tempName, setTempName] = useState("");
  const [tempPhone, setTempPhone] = useState("");

  console.info("Checkout render", {
    editingPersonalDetails,
    tempName,
    tempPhone,
  });

  // User details
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    phone: "",
  });

  // Bill sidebar state
  const [billSidebarOpen, setBillSidebarOpen] = useState(false);
  const [priceBreakdownOpen, setPriceBreakdownOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [shippingQuote, setShippingQuote] = useState(
    calculateShippingQuoteFromSubtotal(0),
  );
  const [shippingQuoteLoading, setShippingQuoteLoading] = useState(false);

  // Order confirmation sidebar state
  const [confirmationSidebarOpen, setConfirmationSidebarOpen] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  // Loading state
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1200);


  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    // Check if user is logged in
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!user || !token) {
      toast.error("Please login to continue");
      navigate("/");
      return;
    }

    // Get user details from localStorage (for email) and API (for name and phone)
    const loadUserData = async () => {
      try {
        const userData = JSON.parse(user);

        // Fetch current profile from API
        const profile = await getMyProfile();

        setUserDetails({
          name: profile.name || "",
          email: profile.email || userData.email || "",
          phone: profile.phone || "",
        });
        setTempName(profile.name || "");
        setTempPhone(profile.phone || "");

        // Auto-open edit state if name or phone is incomplete
        if (!profile.name || !profile.phone) {
          setEditingPersonalDetails(true);
        }
      } catch (e) {
        console.error("Error loading user data", e);
        // Fallback to localStorage if API fails
        try {
          const userData = JSON.parse(user);
          setUserDetails({
            name: userData.name || "",
            email: userData.email || "",
            phone: "",
          });
          setTempName(userData.name || "");
          setTempPhone("");
          setEditingPersonalDetails(true);
        } catch (err) {
          console.error("Error parsing user data", err);
        }
      }
    };

    loadUserData();

    // Load cart from server and addresses
    const loadInitial = async () => {
      try {
        const serverCart = await apiGetCart();
        if (!Array.isArray(serverCart) || serverCart.length === 0) {
          toast.error("Your cart is empty");
          navigate("/");
          return;
        }
        const mapped = serverCart.map((i) => ({
          productId: i.product._id,
          product: i.product.product,
          image: i.product.images?.[0] || "",
          size: i.size || "",
          customSize: i.customSize || "",
          quantity: i.qty ?? i.quantity ?? 1,
          price: i.price ?? i.product.discountedPrice ?? 0,
          originalPrice: i.originalPrice ?? i.product.mrp ?? null,
          discountPercent: i.discountPercent ?? 0,
        }));
        setItems(mapped);
      } catch (e) {
        console.error("Error loading server cart", e);
        toast.error("Error loading cart");
        navigate("/");
        return;
      }

      // Load addresses
      await loadAddresses();
    };

    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadAddresses = async (preferredAddressId = null) => {
    try {
      const list = await getMyAddresses();
      const nextAddresses = list || [];
      setAddresses(nextAddresses);

      if (nextAddresses.length) {
        const defaultAddress = nextAddresses.find((address) => address.isDefault);
        if (
          preferredAddressId &&
          nextAddresses.some(
            (address) => String(address._id) === String(preferredAddressId),
          )
        ) {
          setSelectedAddressId(String(preferredAddressId));
        } else if (defaultAddress?._id) {
          setSelectedAddressId(String(defaultAddress._id));
        } else if (
          !selectedAddressId ||
          !nextAddresses.some(
            (address) => String(address._id) === String(selectedAddressId),
          )
        ) {
          setSelectedAddressId(String(nextAddresses[0]._id));
        }
      } else {
        setSelectedAddressId(null);
        // Auto-open add address modal if no addresses exist
        setAdding(true);
      }
    } catch (e) {
      console.error("could not load addresses", e);
      setAddresses([]);
      // Auto-open add address modal on error (no addresses)
      setAdding(true);
    }
  };

  // Fetch city and state from PIN code
  const fetchLocationFromPin = async (pincode, isEdit = false) => {
    if (!pincode || pincode.length !== 6) {
      if (isEdit) {
        setEditPinError("");
      } else {
        setPinError("");
      }
      return;
    }

    if (isEdit) {
      setEditPinLoading(true);
      setEditPinError("");
    } else {
      setPinLoading(true);
      setPinError("");
    }

    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`,
      );
      const data = await response.json();

      if (
        data &&
        data[0]?.Status === "Success" &&
        data[0]?.PostOffice?.length > 0
      ) {
        const postOffice = data[0].PostOffice[0];
        const city = postOffice.District;
        const state = postOffice.State;

        if (isEdit) {
          setEditAddr((prev) => ({
            ...prev,
            city: city,
            state: state,
          }));
          toast.success("Location details fetched!");
        } else {
          setNewAddr((prev) => ({
            ...prev,
            city: city,
            state: state,
          }));
          toast.success("Location details fetched!");
        }
      } else {
        const errorMsg = "Invalid PIN code or location not found";
        if (isEdit) {
          setEditPinError(errorMsg);
        } else {
          setPinError(errorMsg);
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      const errorMsg = "Failed to fetch location details";
      if (isEdit) {
        setEditPinError(errorMsg);
      } else {
        setPinError(errorMsg);
      }
      toast.error(errorMsg);
    } finally {
      if (isEdit) {
        setEditPinLoading(false);
      } else {
        setPinLoading(false);
      }
    }
  };

  const handleAddAddress = async () => {
    try {
      const savedAddresses = await addMyAddress({ ...newAddr, isDefault: true });
      const savedAddress = Array.isArray(savedAddresses)
        ? savedAddresses.find((address) => address.isDefault) || savedAddresses[savedAddresses.length - 1]
        : null;
      if (Array.isArray(savedAddresses)) {
        setAddresses(savedAddresses);
      }
      setNewAddr({
        label: "",
        name: "",
        line1: "",
        line2: "",
        city: "",
        state: "",
        country: "",
        postalCode: "",
        phone: "",
        isDefault: false,
      });
      setAdding(false);
      if (savedAddress?._id) {
        setSelectedAddressId(String(savedAddress._id));
      }
      toast.success("Address added");
    } catch (e) {
      toast.error("Could not add address");
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // const syncToServer = async (next) => {
  //   const user = localStorage.getItem("user");
  //   const token = localStorage.getItem("token");
  //   if (user && token) {
  //     try {
  //       const formattedCart = next.map((item) => ({
  //         product: item.productId || item.product,
  //         qty: item.quantity || item.qty,
  //         size: item.size,
  //         price: item.price
  //       }));
  //       await apiUpdateCart(formattedCart);
  //     } catch (e) {
  //       console.error("sync cart failed", e);
  //     }
  //   }
  // };

  const subtotal = items.reduce(
    (s, i) => s + (i.price || 0) * (i.quantity || 0),
    0,
  );
  const originalSubtotal = items.reduce(
    (s, i) =>
      s +
      Number(i.originalPrice ?? i.price ?? 0) * Number(i.quantity || 0),
    0,
  );
  const selectedAddress = addresses.find(
    (a) => String(a._id) === String(selectedAddressId),
  );
  const totalPieces = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const itemDiscountTotal = items.reduce((s, i) => {
    const unitMrp = Number(i.originalPrice ?? i.price ?? 0);
    const unitSell = Number(i.price ?? 0);
    return s + Math.max(0, unitMrp - unitSell) * Number(i.quantity || 0);
  }, 0);
  const couponDiscount = Number(appliedCoupon?.couponDiscount || 0);
  const totalDiscount = Number((itemDiscountTotal + couponDiscount).toFixed(2));
  const visibleDiscount = totalDiscount;
  const taxRate = 0.0; // 5% tax (matching backend)
  const taxAmount = Number((subtotal * taxRate).toFixed(2));
  const fallbackShippingQuote = calculateShippingQuoteFromSubtotal(subtotal);
  const effectiveShippingQuote = {
    ...fallbackShippingQuote,
    ...(shippingQuote || {}),
  };
  const shippingPrice = Number(effectiveShippingQuote.shippingPrice || 0);
  const total = subtotal - couponDiscount + taxAmount + shippingPrice;
  const breakdownTotal = Number((originalSubtotal - totalDiscount).toFixed(2));
  const breakdownItems = items.map((item) => ({
    productId: item.productId,
    productName: item.product,
    image: item.image,
    size: item.size,
    quantity: item.quantity,
    unitMrp: Number(item.originalPrice ?? item.price ?? 0),
    discountedPrice: Number(item.price ?? 0),
  }));
  const checkoutPreviewOrder = {
    _id: "CHECKOUT_PREVIEW",
    createdAt: new Date().toISOString(),
    paymentMethod,
    shippingAddress: selectedAddress || {
      name: userDetails.name || "Customer",
      line1: "",
      city: "",
      state: "",
      postalCode: "",
      phone: userDetails.phone || "",
    },
    items: items.map((item) => ({
      productName: item.product,
      image: item.image,
      size: item.size,
      quantity: item.quantity,
      price: item.price,
      originalPrice: Number(item.originalPrice ?? item.price ?? 0),
      discountedPrice: Number(item.price ?? 0),
    })),
    itemsPrice: subtotal,
    couponCode: appliedCoupon?.code,
    couponDiscount,
    itemDiscountTotal,
    totalDiscount,
    taxPrice: taxAmount,
    shippingPrice,
    totalPrice: total,
  };

  useEffect(() => {
    let cancelled = false;

    const fetchShippingQuote = async () => {
      if (!items.length) {
        setShippingQuote(calculateShippingQuoteFromSubtotal(0));
        setShippingQuoteLoading(false);
        return;
      }

      setShippingQuoteLoading(true);
      try {
        const quote = await getShippingQuoteApi({
          items: items.map((i) => ({
            product: i.productId,
            quantity: i.quantity,
          })),
          shippingAddress: selectedAddress
            ? {
                postalCode: selectedAddress.postalCode,
                city: selectedAddress.city,
                state: selectedAddress.state,
                country: selectedAddress.country,
              }
            : undefined,
        });

        if (!cancelled) {
          setShippingQuote(quote);
        }
      } catch (error) {
        console.error("Unable to fetch shipping quote", error);
        if (!cancelled) {
          setShippingQuote(calculateShippingQuoteFromSubtotal(subtotal));
        }
      } finally {
        if (!cancelled) {
          setShippingQuoteLoading(false);
        }
      }
    };

    fetchShippingQuote();

    return () => {
      cancelled = true;
    };
  }, [items, selectedAddress, subtotal]);

  const applyCoupon = async () => {
    const code = String(couponCode || "").trim();
    if (!code) {
      toast.error("Enter a coupon code");
      return;
    }
    if (!items.length) {
      toast.error("Your cart is empty");
      return;
    }

    setCouponLoading(true);
    try {
      const result = await validateCoupon({
        code,
        paymentMethod,
        items: items.map((i) => ({
          product: i.productId,
          quantity: i.quantity,
        })),
      });
      setAppliedCoupon(result);
      setCouponCode(result.code || code.toUpperCase());
      toast.success("Coupon applied");
    } catch (error) {
      setAppliedCoupon(null);
      const backendMsg = error?.response?.data?.message;
      if (backendMsg) {
        // let global api interceptor show the backend message to avoid duplicates
      } else {
        toast.error("Invalid coupon code");
      }
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    toast.success("Coupon removed");
  };

  useEffect(() => {
    if (!appliedCoupon?.code || !items.length) return;
    let cancelled = false;

    const revalidate = async () => {
      try {
        const result = await validateCoupon({
          code: appliedCoupon.code,
          paymentMethod,
          items: items.map((i) => ({
            product: i.productId,
            quantity: i.quantity,
          })),
        });
        if (!cancelled) setAppliedCoupon(result);
      } catch (error) {
        if (!cancelled) {
          setAppliedCoupon(null);
          const backendMsg = error?.response?.data?.message;
          if (backendMsg) {
            // global interceptor will show backend message
          } else {
            toast.error("Coupon removed due to rule mismatch");
          }
        }
      }
    };

    revalidate();
    return () => {
      cancelled = true;
    };
  }, [paymentMethod, items, appliedCoupon?.code]);

  const placeOrder = async () => {
    let addr = selectedAddress;

    // If the user is in the middle of filling out a new address, save it
    // automatically instead of forcing them to hit "Save" first.
    if (adding) {
      if (
        !newAddr.name ||
        !newAddr.phone ||
        !newAddr.line1 ||
        !newAddr.city ||
        !newAddr.state ||
        !newAddr.postalCode
      ) {
        return toast.error("Please fill in all required address fields");
      }

      try {
        const savedAddresses = await addMyAddress({ ...newAddr, isDefault: true });
        const savedAddress = Array.isArray(savedAddresses)
          ? savedAddresses.find((a) => a.isDefault) || savedAddresses[savedAddresses.length - 1]
          : null;
        if (Array.isArray(savedAddresses)) {
          setAddresses(savedAddresses);
        }
        if (savedAddress?._id) {
          setSelectedAddressId(String(savedAddress._id));
        }
        setAdding(false);
        addr = savedAddress;
      } catch (e) {
        return toast.error("Could not save address");
      }
    }

    if (!addr) {
      return toast.error("Please add a complete shipping address");
    }

    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!user || !token) {
      toast.error("Please login to continue");
      return;
    }

    setIsPlacingOrder(true);

    try {
      // Sync cart to server
      await apiUpdateCart(
        items.map((i) => ({
          product: i.productId,
          qty: i.quantity,
          size: i.size,
          customSize: i.customSize || '',
          price: i.price,
        })),
      );

      // Handle COD vs Razorpay
      if (paymentMethod === "COD") {
        // For COD, create order immediately
        const createdOrder = await createOrder({
          items: items.map((i) => ({
            product: i.productId,
            quantity: i.quantity,
            size: i.size,
            customSize: i.customSize || undefined,
          })),
          shippingAddress: addr,
          paymentMethod: paymentMethod, // Use selected payment method
          couponCode: appliedCoupon?.code,
        });
        if (!createdOrder || !createdOrder._id) {
          throw new Error("Order creation failed");
        }
        toast.success("Order placed successfully! Pay on delivery");
        // Clear server cart
        setItems([]);
        await apiUpdateCart([]);
        window.dispatchEvent(new Event("cart:updated"));
        setIsPlacingOrder(false);
        // Show order confirmation sidebar
        setConfirmedOrder({
          _id: createdOrder._id,
          paymentMethod: "Cash on Delivery",
          shippingAddress: addr,
          totalPrice: createdOrder.totalPrice ?? total,
          items: items.map((i) => ({
            productName: i.product,
            image: i.image,
            size: i.size,
            customSize: i.customSize,
            price: i.price,
            quantity: i.quantity,
          })),
        });
        setConfirmationSidebarOpen(true);
        return;
      }

      // Razorpay payment flow
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setIsPlacingOrder(false);
        toast.error("Failed to load payment gateway");
        return;
      }

      // Create Razorpay order
      // Send items/address/coupon so the backend computes the authoritative charge
      // amount itself (instead of trusting this locally-computed `total`), and locks
      // that exact figure in for reuse when the order is saved after payment.
      const userData = JSON.parse(user);
      const razorpayOrderData = await createRazorpayOrder({
        amount: total,
        orderId: undefined,
        items: items.map((i) => ({
          product: i.productId,
          quantity: i.quantity,
        })),
        shippingAddress: addr,
        couponCode: appliedCoupon?.code,
        customerDetails: {
          name: userData.name || addr.name,
          email: userData.email || "",
        },
      });
//W
      const serverAmount = Number(razorpayOrderData.amount) / 100;
      if (Number.isFinite(serverAmount) && Math.abs(serverAmount - total) > 1) {
        toast.error(
          `Total updated to ₹${serverAmount.toFixed(2)} based on current shipping/coupon`,
        );
      }

      // Razorpay checkout options
      const options = {
        key: razorpayOrderData.key,
        amount: razorpayOrderData.amount,
        currency: razorpayOrderData.currency || "INR",
        name: "Nufab",
        description: "Order Payment",
        order_id: razorpayOrderData.orderId,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyData = await verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (!verifyData || !verifyData.success) {
              toast.error("Payment verification failed");
              return;
            }

            // Build paymentResult and create order now
            const paymentResult = {
              id: response.razorpay_payment_id,
              status: verifyData.payment?.status || "paid",
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              method: verifyData.payment?.method || "",
              amount: (verifyData.payment?.amount || 0) / 100,
              captured: verifyData.payment?.captured || false,
              email: verifyData.payment?.email || "",
              contact: verifyData.payment?.contact || "",
            };

            const created = await createOrder({
              items: items.map((i) => ({
                product: i.productId,
                quantity: i.quantity,
                size: i.size,
                customSize: i.customSize || undefined,
              })),
              shippingAddress: addr,
              paymentMethod: "Razorpay",
              paymentResult,
              couponCode: appliedCoupon?.code,
            });
            if (!created || !created._id) {
              throw new Error("Order creation failed");
            }

            toast.success("Payment successful! Order placed");
            // Clear server cart
            setItems([]);
            await apiUpdateCart([]);
            window.dispatchEvent(new Event("cart:updated"));
            // Show order confirmation sidebar
            setConfirmedOrder({
              _id: created._id,
              paymentMethod: "Razorpay (Online Payment)",
              shippingAddress: addr,
              totalPrice: created.totalPrice ?? total,
              items: items.map((i) => ({
                productName: i.product,
                image: i.image,
                size: i.size,
                customSize: i.customSize,
                price: i.price,
                quantity: i.quantity,
              })),
            });
            setConfirmationSidebarOpen(true);
          } catch (err) {
            console.error(err);
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: userData.name || addr.name,
          email: userData.email || "",
          contact: addr.phone || "",
        },
        notes: {
          address: `${addr.line1}, ${addr.city}, ${addr.postalCode}`,
        },
        theme: {
          color: "#000000",
        },
        modal: {
          ondismiss: function () {
            setIsPlacingOrder(false);
            toast.error("Payment cancelled");
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
      setIsPlacingOrder(false); // Set loading false when Razorpay modal opens
    } catch (err) {
      console.error(err);
      setIsPlacingOrder(false);
      toast.error("Could not place order");
    }
  };

  return (
    <div>
      {windowWidth < 1168 ? <HomeMobileNavbar /> : <Navbar />}

    <div className="min-h-screen bg-white font-bdogrotesk">
      <div className="max-w-full  px-2 md:px-4 py-4 md:py-12">
        {/* Header */}
        <div className="mb-5 md:mb-12">
          {/* RESPONSIVE: text-4xl on mobile, text-7xl on desktop */}
          <h1 className="text-black text-2xl sm:text-3xl md:text-4xl lg:text-7xl font-medium mb-2 leading-tight">
            You're about to own a piece stitched just for you. We're excited! Just a few more steps.
          </h1>
          {/* <p className="text-black text-2xl sm:text-3xl md:text-4xl lg:text-7xl font-medium leading-tight">
           
          </p> */}
        </div>

        {/* ======================= Step 01 - Bill ======================= */}
        <div className="w-full border-b border-gray-300 py-5 md:py-20 mb-4 md:mb-12">
          {/* RESPONSIVE: flex-col on mobile, flex-row on desktop */}
          <div className="flex w-full items-start flex-col md:flex-row gap-3 md:gap-0">
            {/* Column 1: Step Indicator (Fixed Width on desktop) */}
            <div className="w-full md:w-32 shrink-0">
              <span className="text-base font-medium text-black">Step 01</span>
            </div>

            {/* Column 2: Big Title */}
            <div className="flex-1 pr-0 md:pr-8 w-full">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-medium text-black tracking-tight">
                Review Order
              </h2>
            </div>

            {/* Column 3 & 4 */}
            <div className="w-full md:w-1/2 shrink-0 flex flex-col md:flex-row justify-between pt-1 gap-6 md:gap-0">
              <div className="grow max-w-xs space-y-1 w-full">
                <div className="flex items-center justify-between text-sm text-black">
                  <span>Total Items</span>
                  <span>{totalPieces}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-black">
                  <span>Order Value</span>
                  <span>₹ {total.toLocaleString("en-IN")}</span>
                </div>
              </div>
              <div className="shrink-0 pt-1">
                <button
                  onClick={() => setPriceBreakdownOpen(true)}
                  className="text-sm font-bold text-black underline underline-offset-4 decoration-1 hover:text-gray-700 uppercase"
                >
                  Price Breakdown
                </button>
              </div>
            </div>
          </div>
        </div>


        <div className="w-full border-b border-gray-300 pb-5 md:pb-12 mb-4 md:mb-12">
          {/* RESPONSIVE: flex-col on mobile, row on desktop */}
          <div className="flex w-full items-start flex-col md:flex-row gap-3 md:gap-0">
            {/* Column 1 */}
            <div className="w-full md:w-32 shrink-0 pt-0 md:pt-3">
              <span className="text-sm font-medium text-black">Step 02</span>
            </div>

            {/* Column 2 */}
            <div className="flex-1 pr-0 md:pr-8 w-full">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-medium text-black -mt-0 md:-mt-2 tracking-tight leading-tight">
                Personal
                <br className="hidden md:block" />{" "}
                Details
              </h2>
            </div>

            {/* Column 3 & 4 */}
            <div className="w-full md:w-1/2 shrink-0 flex flex-col md:flex-row justify-between pt-0 md:pt-3 gap-6 md:gap-0">
              {editingPersonalDetails ? (
                // EDIT STATE
                <>
                  <div className="grow max-w-sm space-y-6 w-full">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-black">
                        Name
                      </label>
                      <input
                        type="text"
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-black">
                        Email
                      </label>
                      <input
                        type="email"
                        value={userDetails.email}
                        disabled
                        className="w-full bg-gray-200 border-none p-2 text-sm text-gray-600 cursor-not-allowed"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-black">
                        Number
                      </label>
                      <input
                        type="tel"
                        value={tempPhone}
                        onChange={(e) => setTempPhone(e.target.value)}
                        className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 items-start px-0 md:px-16 pt-4 md:pt-8 shrink-0">
                    <button
                      onClick={async () => {
                        try {
                          // Call API to update user profile
                          const updatedUser = await updateMyProfile({
                            name: tempName,
                            phone: tempPhone,
                          });

                          // Update local state
                          setUserDetails({
                            ...userDetails,
                            name: updatedUser.name,
                            phone: updatedUser.phone,
                          });

                          // Update only name in localStorage (phone is fetched from API)
                          const user = localStorage.getItem("user");
                          if (user) {
                            const userData = JSON.parse(user);
                            userData.name = updatedUser.name;
                            localStorage.setItem(
                              "user",
                              JSON.stringify(userData)
                            );
                          }

                          setEditingPersonalDetails(false);
                          toast.success("Details updated");
                        } catch (error) {
                          console.error("Error updating profile:", error);
                          toast.error("Failed to update details");
                        }
                      }}
                      className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                    >
                      SAVE
                    </button>
                    <button
                      onClick={() => {
                        setTempName(userDetails.name);
                        setTempPhone(userDetails.phone);
                        setEditingPersonalDetails(false);
                      }}
                      className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                    >
                      CANCEL
                    </button>
                  </div>
                </>
              ) : (
                // VIEW STATE
                <>
                  <div className="space-y-1 text-black grow w-full">
                    <div className="text-base font-medium">
                      {userDetails.name || "No name"}
                    </div>
                    <div className="text-base font-medium">
                      {userDetails.email || "No email"}
                    </div>
                    <div className="text-base font-medium">
                      {userDetails.phone || "No phone"}
                    </div>
                  </div>

                  <div className="px-0 shrink-0 pt-4 md:pt-0">
                    <button
                      onClick={() => setEditingPersonalDetails(true)}
                      className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                    >
                      EDIT
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ======================= Step 03 - Address ======================= */}
        <div className="w-full border-b border-gray-300 pb-5 md:pb-12 mb-4 md:mb-12">
          {/* RESPONSIVE: flex-col on mobile */}
          <div className="flex w-full items-start flex-col md:flex-row gap-3 md:gap-0">
            {/* Column 1 */}
            <div className="w-full md:w-32 shrink-0">
              <span className="text-sm font-medium text-black">Step 03</span>
            </div>

            {/* Column 2 */}
            <div className="flex-1 pr-0 md:pr-8 w-full">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-medium text-black -mt-0 md:-mt-3 tracking-tight">
                Address
              </h2>
            </div>

            {/* Column 3 & 4 */}
            <div className="w-full md:w-1/2 shrink-0 flex flex-col md:flex-row justify-between pt-0 md:pt-1 gap-6 md:gap-0">
              {adding ? (
                // STATE: ADDING NEW ADDRESS
                <>
                  <div className="grow max-w-sm space-y-4 w-full">
                    <input
                      className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={newAddr.name || ""}
                      onChange={(e) =>
                        setNewAddr({ ...newAddr, name: e.target.value })
                      }
                      placeholder="Full Name"
                    />
                    <input
                      className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={newAddr.phone || ""}
                      onChange={(e) =>
                        setNewAddr({ ...newAddr, phone: e.target.value })
                      }
                      placeholder="Phone"
                    />
                    <input
                      className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={newAddr.line1 || ""}
                      onChange={(e) =>
                        setNewAddr({ ...newAddr, line1: e.target.value })
                      }
                      placeholder="Address Line 1"
                    />
                    <input
                      className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={newAddr.line2 || ""}
                      onChange={(e) =>
                        setNewAddr({ ...newAddr, line2: e.target.value })
                      }
                      placeholder="Address Line 2 (Optional)"
                    />
                    <input
                      className="w-full md:w-1/2 bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={newAddr.postalCode || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewAddr({ ...newAddr, postalCode: value });
                        if (value.length === 6) {
                          fetchLocationFromPin(value, false);
                        }
                      }}
                      placeholder="Postal Code"
                      maxLength="6"
                    />
                    {pinLoading && (
                      <div className="text-xs text-blue-600 mt-1">
                        Fetching location...
                      </div>
                    )}
                    {pinError && (
                      <div className="text-xs text-red-600 mt-1">
                        {pinError}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        className="w-1/2 bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                        value={newAddr.city || ""}
                        onChange={(e) =>
                          setNewAddr({ ...newAddr, city: e.target.value })
                        }
                        placeholder="City"
                        readOnly={newAddr.postalCode?.length === 6}
                      />
                      <input
                        className="w-1/2 bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                        value={newAddr.state || ""}
                        onChange={(e) =>
                          setNewAddr({ ...newAddr, state: e.target.value })
                        }
                        placeholder="State"
                        readOnly={newAddr.postalCode?.length === 6}
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 px-0 shrink-0 pt-4 md:pt-0 md:self-start md:ml-6">
                    <button
                      onClick={handleAddAddress}
                      className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                    >
                      SAVE
                    </button>
                    <button
                      onClick={() => {
                        setAdding(false);
                        setNewAddr({
                          label: "",
                          name: "",
                          line1: "",
                          line2: "",
                          city: "",
                          state: "",
                          country: "",
                          postalCode: "",
                          phone: "",
                          isDefault: false,
                        });
                      }}
                      className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                    >
                      CANCEL
                    </button>
                  </div>
                </>
              ) : addresses.length === 0 ? (
                // STATE: NO ADDRESS
                <div className="w-full">
                  <div className="text-base text-black mb-3 font-medium">
                    No address saved
                  </div>
                  <button
                    onClick={() => setAdding(true)}
                    className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                  >
                    ADD ADDRESS
                  </button>
                </div>
              ) : editingAddressId ? (
                // STATE: EDITING ADDRESS
                <>
                  <div className="grow max-w-sm space-y-4 w-full">
                    <input
                      className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={editAddr.name || ""}
                      onChange={(e) =>
                        setEditAddr({ ...editAddr, name: e.target.value })
                      }
                      placeholder="Full Name"
                    />
                    <input
                      className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={editAddr.phone || ""}
                      onChange={(e) =>
                        setEditAddr({ ...editAddr, phone: e.target.value })
                      }
                      placeholder="Phone"
                    />
                    <input
                      className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={editAddr.line1 || ""}
                      onChange={(e) =>
                        setEditAddr({ ...editAddr, line1: e.target.value })
                      }
                      placeholder="Address Line 1"
                    />
                    <input
                      className="w-full bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={editAddr.line2 || ""}
                      onChange={(e) =>
                        setEditAddr({ ...editAddr, line2: e.target.value })
                      }
                      placeholder="Address Line 2 (Optional)"
                    />
                    <input
                      className="w-full md:w-1/2 bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                      value={editAddr.postalCode || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEditAddr({ ...editAddr, postalCode: value });
                        if (value.length === 6) {
                          fetchLocationFromPin(value, true);
                        }
                      }}
                      placeholder="Postal Code"
                      maxLength="6"
                    />
                    {editPinLoading && (
                      <div className="text-xs text-blue-600 mt-1">
                        Fetching location...
                      </div>
                    )}
                    {editPinError && (
                      <div className="text-xs text-red-600 mt-1">
                        {editPinError}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        className="w-1/2 bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                        value={editAddr.city || ""}
                        onChange={(e) =>
                          setEditAddr({ ...editAddr, city: e.target.value })
                        }
                        placeholder="City"
                        readOnly={editAddr.postalCode?.length === 6}
                      />
                      <input
                        className="w-1/2 bg-gray-200 border-none p-2 text-sm focus:ring-0 placeholder-gray-500"
                        value={editAddr.state || ""}
                        onChange={(e) =>
                          setEditAddr({ ...editAddr, state: e.target.value })
                        }
                        placeholder="State"
                        readOnly={editAddr.postalCode?.length === 6}
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 px-0 shrink-0 pt-4 md:pt-0 md:self-start md:ml-6">
                    <button
                      onClick={async () => {
                        try {
                          const addrToUpdate = addresses.find(
                            (a) => String(a._id) === String(editingAddressId),
                          );
                          const updatedAddress = await updateMyAddress(
                            addrToUpdate._id,
                            editAddr,
                          );
                          if (updatedAddress?._id) {
                            setAddresses((current) =>
                              current.map((address) =>
                                String(address._id) === String(updatedAddress._id)
                                  ? updatedAddress
                                  : address,
                              ),
                            );
                            setSelectedAddressId(String(updatedAddress._id));
                          } else {
                            await loadAddresses(editingAddressId);
                          }
                          setEditingAddressId(null);
                          setEditAddr(null);
                          toast.success("Address updated");
                        } catch (e) {
                          toast.error("Could not update address");
                        }
                      }}
                      className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                    >
                      SAVE
                    </button>
                    <button
                      onClick={() => {
                        setEditingAddressId(null);
                        setEditAddr(null);
                      }}
                      className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                    >
                      CANCEL
                    </button>
                  </div>
                </>
              ) : (
                // STATE: VIEW ADDRESS
                (() => {
                  const addr =
                    addresses.find(
                      (a) => String(a._id) === String(selectedAddressId),
                    ) || addresses[0];

                  return (
                    <>
                      <div className="space-y-1 text-black grow w-full">
                        <div className="text-base font-medium">
                          {addr?.name || userDetails.name}
                        </div>
                        <div className="text-base font-medium">
                          {addr?.phone || userDetails.phone}
                        </div>
                        <div className="text-base font-medium">
                          {addr?.line1}
                          {addr?.line2 ? ", " + addr?.line2 : ""}
                          {addr?.city && `, ${addr.city}`}
                        </div>
                        <div className="text-base font-medium">
                          {addr?.state} {addr?.postalCode}
                        </div>
                      </div>

                      <div className="flex gap-4 px-0 shrink-0 pt-4 md:pt-0">
                        <button
                          onClick={() => {
                            setEditingAddressId(String(addr._id));
                            setEditAddr({
                              ...addr,
                              isDefault: !!addr.isDefault,
                            });
                          }}
                          className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                        >
                          EDIT
                        </button>
                        <button
                          onClick={() => setAdding(true)}
                          className="text-sm font-bold text-black underline underline-offset-4 decoration-1 uppercase hover:text-gray-700"
                        >
                          ADD NEW
                        </button>
                      </div>
                    </>
                  );
                })()
              )}
            </div>
          </div>
        </div>

        {/* ======================= Step 04 - Bill ======================= */}
        <div className="w-full border-b border-gray-300 pb-5 md:pb-12 mb-4 md:mb-12">
          <div className="flex w-full items-start flex-col md:flex-row gap-3 md:gap-0">
            <div className="w-full md:w-32 shrink-0 pt-0 md:pt-3">
              <span className="text-sm font-medium text-black">Step 04</span>
            </div>

            <div className="flex-1 pr-0 md:pr-8 w-full">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-medium text-black -mt-0 md:-mt-2 tracking-tight leading-tight">
                Bill
              </h2>
            </div>

            <div className="w-full md:w-1/2 shrink-0 flex flex-col md:flex-row justify-between pt-0 md:pt-3 gap-6 md:gap-0">
              <div className="grow max-w-sm space-y-3 w-full">
                <div className="flex justify-between items-center text-black">
                  <span className="text-base font-medium">Total Pieces</span>
                  <span className="text-base font-bold">{totalPieces}</span>
                </div>

                <div className="flex justify-between items-center text-black">
                  <span className="text-base font-medium">Sub Total</span>
                  <span className="text-base font-bold">₹{originalSubtotal.toLocaleString("en-IN")}</span>
                </div>

                <div className="flex justify-between items-center text-black">
                  <span className="text-base font-medium">Discount</span>
                  <span className="text-base font-bold text-green-700">-₹{visibleDiscount.toLocaleString("en-IN")}</span>
                </div>

                <div className="flex justify-between items-center text-black">
                  <span className="text-base font-medium">Shipping</span>
                  <span className="text-base font-bold">
                    {shippingPrice === 0
                      ? "Free"
                      : `₹${shippingPrice.toLocaleString("en-IN")}`}
                  </span>
                </div>

                <div className="text-xs text-gray-600">
                  {shippingQuoteLoading
                    ? "Calculating shipping..."
                    : effectiveShippingQuote.qualifiesForFreeShipping
                      ? "You have unlocked free shipping."
                      : `Shipping is ₹${Number(
                          effectiveShippingQuote.flatShippingFee || 0,
                        ).toLocaleString("en-IN")}. Add ₹${Number(
                          effectiveShippingQuote.amountForFreeShipping || 0,
                        ).toLocaleString("en-IN")} more to get free shipping above ₹${Number(
                          effectiveShippingQuote.freeShippingThreshold || 0,
                        ).toLocaleString("en-IN")}.`}
                </div>

                <div className="flex justify-between items-center pt-4 mt-1 border-t border-gray-200">
                  <span className="text-base font-bold text-black">Total</span>
                  <span className="text-base font-bold text-black">₹{total.toLocaleString("en-IN")}</span>
                </div>

                <div className="pt-3 space-y-2">
                  <label className="block text-sm font-medium text-black">Coupon Code</label>
                  <div className="flex gap-2">
                    <input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 bg-gray-100 border border-gray-200 p-2 text-sm uppercase"
                    />
                    {!appliedCoupon ? (
                      <button
                        type="button"
                        onClick={applyCoupon}
                        disabled={couponLoading}
                        className="px-3 py-2 bg-black text-white text-sm font-semibold disabled:opacity-50"
                      >
                        {couponLoading ? "Applying" : "Apply"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={removeCoupon}
                        className="px-3 py-2 bg-gray-200 text-black text-sm font-semibold"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {appliedCoupon?.code && (
                    <div className="text-xs text-green-700 font-medium">
                      Coupon {appliedCoupon.code} applied. Savings: ₹{Number(appliedCoupon.couponDiscount || 0).toLocaleString("en-IN")}
                    </div>
                  )}
                </div>
              </div>

              <div className="shrink-0 px-0 pt-4 md:pt-0">
                <button
                  onClick={() => setBillSidebarOpen(true)}
                  className="text-sm font-bold text-black underline underline-offset-4 decoration-1 hover:text-gray-700 uppercase"
                >
                  View Bill
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ======================= Step 05 - Payment Method ======================= */}
        <div className="w-full border-b border-gray-300 pb-5 md:pb-12 mb-4 md:mb-12">
          {/* RESPONSIVE: flex-col on mobile */}
          <div className="flex w-full items-start flex-col md:flex-row gap-3 md:gap-0">
            {/* Column 1 */}
            <div className="w-full md:w-32 shrink-0 pt-0 md:pt-3">
              <span className="text-sm font-medium text-black">Step 05</span>
            </div>

            {/* Column 2 */}
            <div className="flex-1 pr-0 md:pr-8 w-full">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-medium text-black -mt-0 md:-mt-2 tracking-tight leading-tight">
                Payment
                <br className="hidden md:block" /> Method
              </h2>
            </div>

            {/* Column 3 & 4 */}
            <div className="w-full md:w-1/2 shrink-0 flex flex-col md:flex-row justify-between pt-0 md:pt-3 gap-6 md:gap-0">
              <div className="grow space-y-4 w-full">
                {/* Razorpay Option */}
                <div
                  onClick={() => setPaymentMethod("Razorpay")}
                  className={`p-4 border-2 cursor-pointer transition-all ${
                    paymentMethod === "Razorpay"
                      ? "border-black bg-gray-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        paymentMethod === "Razorpay"
                          ? "border-black"
                          : "border-gray-300"
                      }`}
                    >
                      {paymentMethod === "Razorpay" && (
                        <div className="w-3 h-3 rounded-full bg-black"></div>
                      )}
                    </div>
                    <div>
                      <div className="text-base font-semibold text-black">
                        Pay Online (Razorpay)
                      </div>
                      <div className="text-sm text-gray-600">
                        Credit/Debit Card, UPI, Net Banking
                      </div>
                    </div>
                  </div>
                </div>

                {/* COD Option */}
                <div
                  onClick={() => setPaymentMethod("COD")}
                  className={`p-4 border-2 cursor-pointer transition-all ${
                    paymentMethod === "COD"
                      ? "border-black bg-gray-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        paymentMethod === "COD"
                          ? "border-black"
                          : "border-gray-300"
                      }`}
                    >
                      {paymentMethod === "COD" && (
                        <div className="w-3 h-3 rounded-full bg-black"></div>
                      )}
                    </div>
                    <div>
                      <div className="text-base font-semibold text-black">
                        Cash on Delivery (COD)
                      </div>
                      <div className="text-sm text-gray-600">
                        Pay when you receive your order
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-0 md:px-16 shrink-0">
                {/* Empty space or info */}
              </div>
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={placeOrder}
            disabled={
              (!selectedAddressId && addresses.length === 0 && !adding) ||
              isPlacingOrder
            }
            className="group bg-black text-white px-12 py-3 flex justify-between text-base font-semibold tracking-wide disabled:bg-gray-400 disabled:cursor-not-allowed w-full md:w-auto"
          >
            <div className="relative h-5 overflow-hidden w-full md:w-auto text-center">
              <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                <span className="flex font-bdogrotesk h-5 items-center justify-center">
                  {isPlacingOrder
                    ? "Processing..."
                    : paymentMethod === "COD"
                      ? "Place Order (COD)"
                      : "Pay Securely"}
                </span>
                <span className="flex h-5 items-center justify-center">
                  {isPlacingOrder
                    ? "Processing..."
                    : paymentMethod === "COD"
                      ? "Place Order (COD)"
                      : "Pay Securely"}
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>

      {confirm?.open && (
        <ConfirmModal
          open={confirm.open}
          title={confirm.title}
          description={confirm.description}
          onConfirm={confirm.onConfirm}
          onCancel={confirm.onCancel}
        />
      )}

      <OrderConfirmationSidebar
        isOpen={confirmationSidebarOpen}
        onClose={() => setConfirmationSidebarOpen(false)}
        order={confirmedOrder}
      />

      <CheckoutPriceBreakdownSidebar
        isOpen={priceBreakdownOpen}
        onClose={() => setPriceBreakdownOpen(false)}
        items={breakdownItems}
        subtotal={originalSubtotal}
        discount={totalDiscount}
        total={breakdownTotal}
      />

      <BillSidebar
        isOpen={billSidebarOpen}
        onClose={() => setBillSidebarOpen(false)}
        order={checkoutPreviewOrder}
        hideDownloadButton={true}
      />
    </div>
        </div>
  );
}
